import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useProducts } from '../context/ProductContext';
import ProductCard from '../components/ProductCard';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import VideoBanner from '../components/VideoBanner';
import MainBanner from '../components/MainBanner'; // Importar o novo componente
import PromoBanner from '../components/PromoBanner';
import { preloadProductImages } from '../utils/ImagePreloader';
import './HomePage.css';

const HomePage = () => {
  const { products, loading, error, pagination, loadProducts, sortBy, sortOrder, setSortCriteria } = useProducts();
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(24);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  // const [sortOption, setSortOption] = useState('recente'); // Removido - ordenação vem do contexto
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  // Extrair termo de busca da URL
  const searchTerm = new URLSearchParams(window.location.search).get('search') || '';

  // Resetar paginação ao mudar termo de busca
  useEffect(() => {
    setCurrentPage(1);
    setVisibleCount(24);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchTerm]);

  // const sortedProducts = useMemo(() => { ... }); // Removido - produtos já vêm ordenados do contexto

  // Carrega produtos (incluindo busca) ao alterar página, termo de busca ou ordenação
  useEffect(() => {
    console.log('HomePage carregando produtos, searchTerm:', searchTerm, 'sortBy:', sortBy, 'sortOrder:', sortOrder);
    // Quando a ordenação muda, setSortCriteria no contexto já reseta a paginação para 1.
    // Se quisermos que HomePage também resete sua currentPage local para 1 ao mudar a ordenação:
    // if (sortBy !== prevSortBy || sortOrder !== prevSortOrder) { setCurrentPage(1); }
    // Por ora, vamos manter o currentPage local e deixar o loadProducts buscar a página atual com a nova ordenação.
    loadProducts(currentPage, pagination.limit, null, searchTerm);
  }, [currentPage, loadProducts, pagination.limit, searchTerm, sortBy, sortOrder]); // Adicionado sortBy e sortOrder

  // Atualiza produtos exibidos com base no visibleCount
  useEffect(() => {
    setDisplayedProducts(products.slice(0, visibleCount));
    setAllLoaded(visibleCount >= products.length);
    
    // Pré-carregar imagens dos próximos produtos que serão exibidos
    if (products.length > visibleCount) {
      const nextProducts = products.slice(visibleCount, visibleCount + 8);
      preloadProductImages(nextProducts);
    }
  }, [products, visibleCount]);

  // Função para mudar de página
  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      setVisibleCount(24);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Carrega mais produtos no scroll
  const loadMoreProducts = useCallback(() => {
    if (loadingMore || allLoaded) return;
    setLoadingMore(true);
    setVisibleCount(prev => Math.min(prev + 8, products.length));
    setLoadingMore(false);
  }, [loadingMore, allLoaded, products.length]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (loadingRef.current) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => entry.isIntersecting && loadMoreProducts(),
        { threshold: 0.1, rootMargin: '200px' }
      );
      observerRef.current.observe(loadingRef.current);
    }
    return () => observerRef.current && observerRef.current.disconnect();
  }, [loadMoreProducts]);

  // Paginação simples
  const renderPagination = () => {
    const { totalPages } = pagination;
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    if (start > 1) pages.push(<button key="first" onClick={() => handlePageChange(1)} className="pagination-button">«</button>);
    for (let i = start; i <= end; i++) {
      pages.push(<button key={i} onClick={() => handlePageChange(i)} className={`pagination-button ${i === currentPage ? 'active' : ''}`}>{i}</button>);
    }
    if (end < totalPages) pages.push(<button key="last" onClick={() => handlePageChange(totalPages)} className="pagination-button">»</button>);
    return (
      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-button">Anterior</button>
        <div className="pagination-numbers">{pages}</div>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-button">Próxima</button>
      </div>
    );
  };

  // Renderizar esqueletos de carregamento quando estiver carregando e não houver produtos
  if (loading && products.length === 0) {
    return (
      <div className="container" style={{ paddingTop: '25px' }}>
        {!searchTerm && <VideoBanner />}
        <div className="home-header">
          <h2>Nossos Produtos</h2>
          <p>Carregando nossa seleção de produtos...</p>
        </div>
        <div className="products-grid">
          {Array(12).fill(0).map((_, index) => (
            <div key={`skeleton-${index}`} className="product-grid-item">
              <ProductCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (error) return <div className="error">{error}</div>;
  if (!loading && products.length === 0) {
    return (
      <div className="container">
        <div className="no-products">
          <h2>Nenhum produto encontrado</h2>
          {searchTerm ? (
            <div>
              <p>Não encontramos produtos para o termo <strong>"{searchTerm}"</strong>.</p>
              <button className="return-button" onClick={() => { loadProducts(1); window.history.pushState({}, '', '/'); }}>
                Voltar para todos os produtos
              </button>
            </div>
          ) : (
            <p>Tente novamente mais tarde.</p>
          )}
        </div>
      </div>
    );
  }

  // Preparar os produtos para exibição com transição suave
  const renderProducts = () => {
    // Se estiver carregando mais produtos (não o carregamento inicial), mostrar os produtos atuais
    // e adicionar esqueletos no final para indicar que mais estão sendo carregados
    if (loadingMore) {
      return (
        <>
          {displayedProducts.map(p => (
            <div key={p.id} className="product-grid-item">
              <ProductCard product={p} />
            </div>
          ))}
          {Array(4).fill(0).map((_, index) => (
            <div key={`loading-more-${index}`} className="product-grid-item">
              <ProductCardSkeleton />
            </div>
          ))}
        </>
      );
    }
    
    // Caso contrário, mostrar apenas os produtos carregados
    return displayedProducts.map(p => (
      <div key={p.id} className="product-grid-item">
        <ProductCard product={p} />
      </div>
    ));
  };

  return (
    <div className="container" style={{ paddingTop: '25px' }}>
      {!searchTerm && <VideoBanner />}
      <MainBanner /> {/* Adicionar o MainBanner aqui */}
      <PromoBanner products={products} />
      <div className="sort-filter">
        <label htmlFor="sort-select">Ordenar por: </label>
        <select
          id="sort-select"
          value={`${sortBy}-${sortOrder}`}
          onChange={e => {
            const [newSortBy, newSortOrder] = e.target.value.split('-');
            setCurrentPage(1); // Resetar a página atual local para 1
            setVisibleCount(24); // Resetar a contagem de produtos visíveis
            setSortCriteria(newSortBy, newSortOrder); // Atualizar o critério de ordenação no contexto
          }}
        >
          <option value="created_at-DESC">Mais Recentes</option>
          <option value="name-ASC">Nome (A-Z)</option>
          <option value="name-DESC">Nome (Z-A)</option>
          <option value="price-ASC">Menor Preço</option>
          <option value="price-DESC">Maior Preço</option>
        </select>
      </div>
      <div className="home-header">
        <h2>Nossos Produtos</h2>
        <p>Confira nossa seleção de produtos disponíveis</p>
      </div>
      <div className="products-grid">
        {renderProducts()}
      </div>
      {!allLoaded && (
        <div className="loading-more" ref={loadingRef}>
          {loadingMore ? (
            <div className="loading-more-indicator">
              <span className="loading-spinner" />
              <p>Carregando mais produtos...</p>
            </div>
          ) : (
            <p>Role para ver mais produtos</p>
          )}
        </div>
      )}
      {pagination.totalPages > 1 && renderPagination()}
    </div>
  );
};

export default HomePage;
